import { useRef, useState } from 'react'
import styled from 'styled-components'

import { Button, Divider, Form, FormInstance, Input, Switch, notification } from 'antd'

import * as CmsPageService from '../../../../../services/bb-api/oplevalt/cms/page'
import type { Page } from '../../../../../services/bb-api/oplevalt/cms/page'

import { txt } from '../../../../../locales/i18n'
import { formatDate } from '../../../../../utils/date'

export type PageFormProps = {
	visible: boolean
	page: Page
	setPage: (page: Page) => void
}

export const PageForm = (props: PageFormProps) => {
	const [isUpdating, setIsUpdating] = useState(false)
	const formRef = useRef<FormInstance>(null)

	const handleSavePage = async (pageValues: any) => {
		const form = formRef.current
		if (form === null) return

		const [urlError = null] = form.getFieldError('url')
		if (urlError !== null) return notification.error({ message: urlError })

		setIsUpdating(true)

		try {
			const page = await CmsPageService.update(props.page.id, pageValues)
			props.setPage(page)

			notification.success({ message: txt('notification.update successful') })
		} catch (error) {
			if (error instanceof Error && error.message === 'url already exists') {
				const errorMessage = txt('cms.notifications.urlExists')
				form.setFields([{ name: 'url', errors: [errorMessage] }])
			}

			notification.error({ message: txt('notification.update failed') })
		}

		setIsUpdating(false)
	}

	const handleValuesChange = (values: any) => {
		const form = formRef.current
		if (form === null) return

		const [[key, value]] = Object.entries(values)

		if (key === 'title') {
			form.setFields([{ name: key, errors: [] }])

			const maxTitleLength = 60
			const titleLength = (value as string).length
			if (titleLength < maxTitleLength) return

			const errorMessage = txt('cms.notifications.titleLength', {
				maxTitleLength,
			})

			form.setFields([{ name: key, errors: [errorMessage] }])
		}

		if (key === 'metaDescription') {
			form.setFields([{ name: key, errors: [] }])

			const maxMetaDescriptionLength = 160
			const metaDescriptionLength = (value as string).length
			if (metaDescriptionLength < maxMetaDescriptionLength) return

			const errorMessage = txt('cms.notifications.metaDescriptionLength', {
				maxMetaDescriptionLength,
			})

			form.setFields([{ name: key, errors: [errorMessage] }])
		}

		if (key === 'url') {
			form.setFields([{ name: key, errors: [] }])
			const urlPattern = /^\w[/\w-]*$/gi
			if (urlPattern.test(value as string)) return

			const errorMessage = txt('cms.notifications.urlFormat')
			form.setFields([{ name: key, errors: [errorMessage] }])
		}
	}

	if (props.visible === false) return null

	return (
		<FormStyled initialValues={props.page} onFinish={handleSavePage} onValuesChange={handleValuesChange} ref={formRef}>
			<div className="inputs-container">
				<h3>{txt('cms.general')}</h3>

				<label>{txt('cms.title')}</label>
				<Form.Item name="title" style={{ width: '100%' }}>
					<Input.TextArea autoSize showCount required style={{ width: '100%' }} />
				</Form.Item>

				<label>{txt('cms.url')}</label>
				<Form.Item name="url">
					<Input required />
				</Form.Item>

				<label>{txt('cms.metaDescription')}</label>
				<Form.Item name="metaDescription" style={{ width: '100%' }}>
					<Input.TextArea autoSize showCount required style={{ width: '100%' }} />
				</Form.Item>

				<label>{txt('cms.published')}</label>
				<Form.Item name="published" valuePropName="checked">
					<Switch />
				</Form.Item>

				<Divider />
				<h3>{txt('cms.layoutNames.header spacer')}</h3>

				<div className="row-container">
					<div className="flex-1-container">
						<label>{txt('cms.visible')}</label>
						<Form.Item name={['content', 'headerSpacer', 'visible']} valuePropName="checked">
							<Switch />
						</Form.Item>
					</div>

					<div className="flex-1-container">
						<label>{txt('cms.background')}</label>
						<Form.Item name={['content', 'headerSpacer', 'background']}>
							<Input type="color" />
						</Form.Item>
					</div>
				</div>

				<Divider />

				<div className="date-container">
					<div>
						<label>{txt('cms.created')}</label>
						<div>{formatDate(new Date(props.page.created))}</div>
					</div>

					<div>
						<label>{txt('cms.updated')}</label>
						<div>{formatDate(new Date(props.page.updated))}</div>
					</div>
				</div>
			</div>

			<div className="submit-container">
				<Button type="primary" htmlType="submit" loading={isUpdating}>
					{txt('cms.updatePage')}
				</Button>
			</div>
		</FormStyled>
	)
}

const FormStyled = styled(Form)`
	height: 100%;

	.inputs-container {
		padding: 2ch;
		overflow-y: scroll;
	}

	.row-container {
		display: flex;
		gap: 1rem;
	}

	.flex-1-container {
		flex: 1;
	}

	.centered-content-container {
		justify-content: center;
	}

	.flex-vertical {
		display: flex;
		flex-direction: column;
		gap: 1rem;
	}

	.date-container {
		display: flex;
		justify-content: space-between;
		align-items: center;

		> div {
			display: flex;
			flex-direction: column;
		}
	}

	.submit-container {
		border-top: 1px solid #eee;
		display: flex;
		gap: 1ch;
		padding-top: 1rem;
		justify-content: flex-end;
		margin-top: auto;
		padding: 2ch;
	}
`
